/**
 * useChartLib — read/write the active chart library selection.
 *
 * Persisted in localStorage at key `insights.chartLib`. Default: 'recharts'.
 * Updated via the Dev Console slicer; consumed by `<Chart>` to dispatch to
 * the matching library implementation.
 *
 * Only `role === 'dev'` users see the slicer. Non-dev users always render
 * with the default library — there is no per-tenant chart-lib persistence,
 * only per-user (per-browser).
 */

import { useEffect, useState, useCallback } from 'react';
import { CHART_LIBS, DEFAULT_CHART_LIB, type ChartLib } from './types';

const STORAGE_KEY = 'insights.chartLib';

function readStoredLib(): ChartLib {
  if (typeof window === 'undefined') return DEFAULT_CHART_LIB;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw && (CHART_LIBS as readonly string[]).includes(raw)) {
    return raw as ChartLib;
  }
  return DEFAULT_CHART_LIB;
}

/**
 * React hook. Returns [activeLib, setLib]. setLib persists to localStorage
 * and broadcasts via a custom event so other mounted instances of the hook
 * re-read on the same page.
 */
export function useChartLib(): [ChartLib, (lib: ChartLib) => void] {
  const [lib, setLibState] = useState<ChartLib>(readStoredLib);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setLibState(readStoredLib());
    };
    const onCustom = () => setLibState(readStoredLib());
    window.addEventListener('storage', onStorage);
    window.addEventListener('insights:chartLibChanged', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('insights:chartLibChanged', onCustom);
    };
  }, []);

  const setLib = useCallback((next: ChartLib) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, next);
    window.dispatchEvent(new Event('insights:chartLibChanged'));
    setLibState(next);
  }, []);

  return [lib, setLib];
}
